/**
 * HealthProfile — Clean, scannable health profile.
 * Merges all health:profile stats. Matches the design spec:
 * - Overview with bullet points and bold key terms
 * - Basics in two-column grid
 * - Conditions with dot indicators
 * - Medications with Daily/As Needed sub-sections and teal left-border
 * - Color-coded section headers
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import { ModuleEmptyState } from '../_infrastructure/ModuleEmptyState';

const SYSTEM_KEYS = new Set([
  'source', 'date', 'id', 'type', 'title', 'createdAt', 'updatedAt', 'visibility',
  'version', 'moduleId', 'moduleIds', 'conversationIds', 'label', 'value', 'metricKey',
  'category', 'created_at', 'updated_at', '_etag', 'module_ids', 'conversation_ids',
  'what', 'name_lower', 'completed', 'completed_at', 'status', 'priority', 'due',
  'dueAt', 'content', 'body', 'name', 'objectType', 'objectTypes', 'archivedAt', 'archived_at',
]);
const JUNK_VALUES = new Set(['Untitled', 'untitled', 'undefined', 'null', 'N/A', '']);

function calcAge(dob: string): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

function formatDate(d: string): string {
  try {
    const date = new Date(d + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
}

function splitItems(val: unknown): string[] {
  if (!val) return [];
  return String(val).split(',').map(s => s.trim()).filter(Boolean);
}

/** Parse "Drug Name dosage" into { name, dosage } */
function parseMedication(med: string): { name: string; dosage: string } {
  // Try to split on common dosage patterns
  const match = med.match(/^(.+?)\s+(\d+[\d./]*\s*(?:mg|mcg|ml|units?|tablet|capsule|inhaler).*)/i);
  if (match) return { name: match[1].trim(), dosage: match[2].trim() };
  return { name: med, dosage: '' };
}

/** Generate overview bullet points with bold key terms */
function generateOverviewBullets(p: Record<string, unknown>): Array<{ text: string; bolds: string[] }> {
  const bullets: Array<{ text: string; bolds: string[] }> = [];
  const age = p.age || (p.birthday || p.dateOfBirth ? calcAge(String(p.birthday || p.dateOfBirth)) : null);

  if (typeof age === 'number' && age > 0) {
    if (age >= 60) bullets.push({ text: `At ${age}, prioritize joint health, flexibility, and consistent sleep patterns.`, bolds: ['joint health', 'flexibility'] });
    else if (age >= 50) bullets.push({ text: `At ${age}, focus on maintaining muscle mass, bone density, and cardiovascular health.`, bolds: ['muscle mass', 'bone density', 'cardiovascular health'] });
    else if (age >= 40) bullets.push({ text: `At ${age}, recovery becomes more important — don't skip rest days.`, bolds: ['recovery'] });
  }

  const conditions = String(p.conditions || p.diagnoses || '');
  if (conditions) {
    bullets.push({ text: `Managing ${conditions} — track symptoms and adjust activity accordingly.`, bolds: [conditions] });
  }

  const smoking = p.smokingHistory ? String(p.smokingHistory) : '';
  if (smoking) {
    bullets.push({ text: `Smoked age 22–38. Lung capacity may be a factor — cardio helps rebuild.`, bolds: ['Lung capacity'] });
  }

  const injuries = splitItems(p.injuryHistory);
  if (injuries.length > 0) {
    const injuryText = injuries.map(i => {
      const parts = i.match(/(.+?)\s+([\w]+\s+\d{4})$/);
      return parts ? `${parts[1].toLowerCase()} (${parts[2]})` : i.toLowerCase();
    });
    bullets.push({ text: `Past injuries: ${injuryText.join(' and ')} — be mindful during high-impact activities.`, bolds: injuryText.map(i => i.split('(')[0].trim()) });
  }

  if (bullets.length === 0) {
    bullets.push({ text: 'Add more details to your profile for personalized health guidance.', bolds: [] });
  }

  return bullets;
}

/** Render text with certain substrings bolded */
function BoldedText({ text, bolds }: { text: string; bolds: string[] }) {
  if (bolds.length === 0) return <>{text}</>;
  // Build regex to match any bold term
  const escaped = bolds.map(b => b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) => {
        const isBold = bolds.some(b => b.toLowerCase() === part.toLowerCase());
        return isBold ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>;
      })}
    </>
  );
}

export default function HealthProfile() {
  const { token, profile: sessionProfile } = useSession();

  const { data: mergedProfile, isLoading } = useQuery({
    queryKey: ['health-profile'],
    queryFn: async () => {
      const result = await api.objects.getObjectsByType(token ?? null, 'stat', { limit: 200 });
      const items = result?.items || [];
      const profileStats = items.filter((s: any) =>
        (s.source || s.attributes?.source) === 'health:profile'
      );
      if (profileStats.length === 0) return null;

      const merged: Record<string, unknown> = {};
      for (const stat of profileStats) {
        for (const [key, val] of Object.entries(stat)) {
          if (SYSTEM_KEYS.has(key)) continue;
          if (val != null && val !== '' && val !== 'health:profile' && val !== 'profile' && !JUNK_VALUES.has(String(val))) {
            merged[key] = val;
          }
        }
      }
      return merged;
    },
    enabled: Boolean(token),
  });

  if (isLoading) return null;
  if (!mergedProfile || Object.keys(mergedProfile).length === 0) return <EmptyProfilePrompt />;

  const p = mergedProfile;
  const age = p.age || (p.birthday || p.dateOfBirth ? calcAge(String(p.birthday || p.dateOfBirth)) : null);
  const overviewBullets = generateOverviewBullets(p);

  // Basics fields
  const basics: Array<{ label: string; value: string }> = [];
  const patientName = p.patientName ? String(p.patientName) : (sessionProfile?.name || '');
  if (patientName) basics.push({ label: 'Name', value: patientName });
  const bday = p.birthday || p.dateOfBirth || p.dob;
  if (bday) basics.push({ label: 'Birthday', value: formatDate(String(bday)) });
  const sex = p.sex || p.gender;
  if (sex) basics.push({ label: 'Sex', value: String(sex).charAt(0).toUpperCase() + String(sex).slice(1) });
  if (p.height || p.heightDisplay) basics.push({ label: 'Height', value: String(p.heightDisplay || p.height) });
  if (p.weight || p.weightDisplay) basics.push({ label: 'Weight', value: String(p.weightDisplay || p.weight) });
  if (p.bloodType) basics.push({ label: 'Blood Type', value: String(p.bloodType) });
  if (age && !basics.some(b => b.label === 'Age')) basics.push({ label: 'Age', value: `${age}` });

  // Conditions
  const conditions = splitItems(p.conditions || p.diagnoses);

  // Medications
  const dailyMeds = splitItems(p.dailyMedications).map(parseMedication);
  const asNeededMeds = splitItems(p.asNeededMedications).map(parseMedication);
  const genericMeds = splitItems(p.medications || p.meds).map(parseMedication);
  const hasMeds = dailyMeds.length > 0 || asNeededMeds.length > 0 || genericMeds.length > 0;

  // Allergies
  const allergies = splitItems(p.allergies);

  // History
  const injuries = splitItems(p.injuryHistory);
  const smoking = p.smokingHistory ? String(p.smokingHistory) : '';
  const hospitalizations = splitItems(p.hospitalizations);
  const surgeries = splitItems(p.surgeries);
  const hasHistory = injuries.length > 0 || smoking || hospitalizations.length > 0 || surgeries.length > 0;

  // Emergency
  const emergencyContact = p.emergencyContact ? String(p.emergencyContact) : '';
  const emergencyPhone = p.emergencyPhone ? String(p.emergencyPhone) : '';

  const card = (children: React.ReactNode) => (
    <div style={{
      background: 'var(--color-surface)', borderRadius: 12,
      padding: '14px 16px', marginBottom: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {children}
    </div>
  );

  const sectionHeader = (title: string) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
      {title}
    </div>
  );

  const subHeader = (title: string) => (
    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
      {title}
    </div>
  );

  const MedCard = ({ meds, borderColor }: { meds: Array<{ name: string; dosage: string }>; borderColor: string }) => (
    <>
      {meds.map((med, i) => (
        <div key={i} style={{ borderLeft: `3px solid ${borderColor}`, paddingLeft: 10, marginBottom: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{med.name}</div>
          {med.dosage && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{med.dosage}</div>}
        </div>
      ))}
    </>
  );

  // Avatar initial
  const initial = patientName ? patientName.charAt(0).toUpperCase() : '?';
  const sexStr = sex ? String(sex).charAt(0).toUpperCase() + String(sex).slice(1) : '';

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Health Overview — teal left-border card with dividers */}
      {card(
        <div style={{ borderLeft: '3px solid #0ea5e9', paddingLeft: 14, marginLeft: -16 }}>
          {sectionHeader('Health Overview')}
          {overviewBullets.map((bullet, i) => (
            <div key={i} style={{
              fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6,
              padding: '8px 0',
              borderTop: i > 0 ? '1px solid var(--color-border, #e5e7eb)' : 'none',
            }}>
              <BoldedText text={bullet.text} bolds={bullet.bolds} />
            </div>
          ))}
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: 12 }}>
        {/* LEFT COLUMN — Conditions + Medications */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {conditions.length > 0 && card(
            <>
              {sectionHeader('Conditions')}
              {conditions.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{c}</span>
                </div>
              ))}
            </>
          )}

          {hasMeds && card(
            <>
              {sectionHeader('Medications')}
              {dailyMeds.length > 0 && (
                <>
                  {subHeader('Daily')}
                  <MedCard meds={dailyMeds} borderColor="#0ea5e9" />
                </>
              )}
              {dailyMeds.length > 0 && asNeededMeds.length > 0 && (
                <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', margin: '8px 0' }} />
              )}
              {asNeededMeds.length > 0 && (
                <>
                  {subHeader('As needed')}
                  <MedCard meds={asNeededMeds} borderColor="#f59e0b" />
                </>
              )}
              {genericMeds.length > 0 && dailyMeds.length === 0 && asNeededMeds.length === 0 && (
                <MedCard meds={genericMeds} borderColor="#0ea5e9" />
              )}
            </>
          )}

          {allergies.length > 0 && card(
            <>
              {sectionHeader('Allergies')}
              {allergies.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{a}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* RIGHT COLUMN — Basics (avatar) + History */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {basics.length > 0 && card(
            <>
              {/* Avatar + name header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(14, 165, 233, 0.12)', color: '#0ea5e9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 600, flexShrink: 0,
                }}>
                  {initial}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}>{patientName || 'You'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                    {[sexStr, age ? `${age} years old` : ''].filter(Boolean).join(' \u00b7 ')}
                  </div>
                </div>
              </div>
              {/* Basics rows */}
              {basics.filter(b => b.label !== 'Name').map((b, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                  borderTop: '1px solid var(--color-border, #e5e7eb)',
                }}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{b.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{b.value}</span>
                </div>
              ))}
            </>
          )}

          {hasHistory && card(
            <>
              {sectionHeader('History')}
              {injuries.length > 0 && (
                <>
                  {subHeader('Injuries')}
                  {injuries.map((inj, i) => (
                    <div key={i} style={{
                      fontSize: 13, color: 'var(--color-text-primary)', padding: '4px 0',
                      borderTop: i > 0 ? '1px solid var(--color-border, #e5e7eb)' : 'none',
                    }}>
                      {inj.replace(/\s+([\w]+\s+\d{4})$/, ' \u00b7 $1')}
                    </div>
                  ))}
                </>
              )}
              {smoking && (
                <div style={{ marginTop: injuries.length > 0 ? 10 : 0 }}>
                  {subHeader('Smoking')}
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{smoking}</div>
                </div>
              )}
              {hospitalizations.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {subHeader('Hospitalizations')}
                  {hospitalizations.map((h, i) => (
                    <div key={i} style={{ fontSize: 13, color: 'var(--color-text-primary)', padding: '4px 0' }}>{h}</div>
                  ))}
                </div>
              )}
              {surgeries.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {subHeader('Surgeries')}
                  {surgeries.map((s, i) => (
                    <div key={i} style={{ fontSize: 13, color: 'var(--color-text-primary)', padding: '4px 0' }}>{s}</div>
                  ))}
                </div>
              )}
            </>
          )}

          {(emergencyContact || emergencyPhone) && card(
            <>
              {sectionHeader('Emergency Contact')}
              {emergencyContact && <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{emergencyContact}</div>}
              {emergencyPhone && <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{emergencyPhone}</div>}
            </>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textAlign: 'center', fontStyle: 'italic', padding: '4px 0' }}>
        Update your profile in chat
      </div>
    </div>
  );
}

function EmptyProfilePrompt() {
  return (
    <ModuleEmptyState
      icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
      iconColor="#ec4899"
      title="Personalize your health tracking"
      description={<>Tell Xavior about yourself so it can give you personalized insights.<br />Try: &quot;I&apos;m 52, male, 175 lbs, I take amlodipine for blood pressure&quot;</>}
    />
  );
}
